import { generatePath } from '@/services/router/generate-path';
import type { AttributeScope } from '@/services/db/data-index/entity';
import type { DataIndexItemKind, DataIndexType } from './utils/types';

export { asDataIndexType } from './utils/types';

/**
 * The data index's URLs, and the correspondence between the browser a URL
 * names and the endpoints that serve it.
 *
 * Every page is served at two spellings while the migration runs: the
 * migrated `/data-index`, and the `journeys/data-index` the embedded app
 * answers.
 */

export function dataIndexListPath(workspaceId: string, type: DataIndexListType): string {
  return generatePath('/workspaces/:workspaceId/data-index/:type', { workspaceId, type });
}

export function dataIndexPath(workspaceId: string): string {
  return generatePath('/workspaces/:workspaceId/data-index', { workspaceId });
}

export function legacyDataIndexListPath(workspaceId: string, type: DataIndexType): string {
  return generatePath('/workspaces/:workspaceId/journeys/data-index/:type', { workspaceId, type });
}

/**
 * An item's own page: two levels for a person attribute or event, three for
 * an object or relationship attribute, whose name only means something under
 * its object type.
 *
 * The name is customer-authored and can contain anything, including the
 * characters that would otherwise end the segment, so it is escaped here —
 * `generatePath` substitutes params verbatim.
 */
function itemType(kind: DataIndexItemKind, scope: AttributeScope): DataIndexType {
  if (scope.kind === 'relationship') {
    return 'object-relationship-attributes';
  }
  if (scope.kind === 'object') {
    return 'object-attributes';
  }
  return kind === 'event' ? 'events' : 'attributes';
}

export function dataIndexItemPath(
  workspaceId: string,
  kind: DataIndexItemKind,
  name: string,
  scope: AttributeScope,
): string {
  const itemId = encodeURIComponent(name);
  const type = itemType(kind, scope);

  if (scope.kind === 'profile') {
    return generatePath('/workspaces/:workspaceId/data-index/:type/:itemId', { workspaceId, type, itemId });
  }

  return generatePath('/workspaces/:workspaceId/data-index/:type/:itemId/:nestedItemId', {
    workspaceId,
    type,
    itemId: String(scope.objectTypeId),
    nestedItemId: itemId,
  });
}

export function legacyDataIndexItemPath(
  workspaceId: string,
  kind: DataIndexItemKind,
  name: string,
  scope: AttributeScope,
): string {
  const itemId = encodeURIComponent(name);
  const type = itemType(kind, scope);

  if (scope.kind === 'profile') {
    return generatePath('/workspaces/:workspaceId/journeys/data-index/:type/:itemId', { workspaceId, type, itemId });
  }

  return generatePath('/workspaces/:workspaceId/journeys/data-index/:type/:itemId/:nestedItemId', {
    workspaceId,
    type,
    itemId: String(scope.objectTypeId),
    nestedItemId: itemId,
  });
}

/**
 * What an item URL addresses: which family of attributes serves it, and what
 * the item is called.
 *
 * The two shapes share their route params, because a route tree cannot give
 * the same position two names. On a two-level URL `itemId` is the item; on a
 * three-level one it is the object type, and `nestedItemId` is the item.
 */
export type DataIndexItemAddress = {
  kind: DataIndexItemKind;
  scope: AttributeScope;
  /** The attribute or event name, as the endpoints spell it. */
  name: string;
};

export function dataIndexItemAddress(
  type: DataIndexType,
  itemId: string,
  nestedItemId?: string,
): DataIndexItemAddress | null {
  const isNested = type === 'object-attributes' || type === 'object-relationship-attributes';

  if (!isNested) {
    // A name is the whole of a two-level URL's last segment; a second one
    // means the URL addresses something this browser does not serve.
    if (nestedItemId != null) {
      return null;
    }
    return {
      kind: type === 'events' ? 'event' : 'attribute',
      scope: { kind: 'profile' },
      name: itemId,
    };
  }

  const objectTypeId = Number(itemId);
  if (nestedItemId == null || !Number.isInteger(objectTypeId) || objectTypeId <= 0) {
    return null;
  }

  return type === 'object-relationship-attributes'
    ? { kind: 'object-relationship-attribute', scope: { kind: 'relationship', objectTypeId }, name: nestedItemId }
    : { kind: 'object-attribute', scope: { kind: 'object', objectTypeId }, name: nestedItemId };
}

/**
 * The browser the landing URL opens on.
 *
 * Constrained to a list both apps serve, because the landing URL resolves to
 * it in either — a default only this app had would strand everyone the
 * migration flag is off for.
 */
export const DEFAULT_DATA_INDEX_TYPE = 'attributes' as const satisfies DataIndexListType & DataIndexType;

/**
 * The kind of row a browser is showing.
 *
 * The URL segment alone does not settle it: choosing an object type narrows
 * the attributes browser without changing the path, so a row's kind follows
 * the scope. Only the events browser is decided by the segment, because
 * events have no object type to narrow by.
 */
export function itemKindOf(type: DataIndexType, scope: AttributeScope): DataIndexItemKind {
  if (type === 'events') {
    return 'event';
  }
  switch (scope.kind) {
    case 'profile':
      return 'attribute';
    case 'object':
      return 'object-attribute';
    case 'relationship':
      return 'object-relationship-attribute';
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${String(_exhaustive)}`);
    }
  }
}

/** The segment the conversions list is served at. */
export const CONVERSIONS_LIST_TYPE = 'conversions';

/**
 * A browser that has a list of its own at `/data-index/:type`. The object
 * segments are not among them: their rows are read by choosing an object type
 * on the attributes list, which writes the query rather than the path, and
 * their own URLs stay with the embedded app.
 *
 * Conversions is not a `DataIndexType`, which is deliberate: it is the one
 * list the embedded app has no URL for, so the type system refuses to hand a
 * reader to a `journeys/data-index/conversions` that answers nothing.
 */
export type DataIndexListType = 'attributes' | 'events' | typeof CONVERSIONS_LIST_TYPE;

export type DataIndexTab = {
  type: DataIndexListType;
  label: string;
};

/**
 * The two browsers every workspace has. The object ones have no tab of their
 * own: they are reached by choosing an object type while on Attributes, so
 * they keep that tab active.
 */
export const DATA_INDEX_TABS = [
  { type: 'attributes', label: 'Attributes' },
  { type: 'events', label: 'Events' },
] as const satisfies readonly { type: DataIndexType; label: string }[];

/** The workspace's goals, read as the conversions they count. */
const CONVERSIONS_TAB = { type: CONVERSIONS_LIST_TYPE, label: 'Conversions' } as const satisfies DataIndexTab;

/**
 * The tabs a reader is offered.
 *
 * Conversions is behind `goals-to-conversions`, and the flag decides whether
 * the tab exists at all rather than what it shows: with the flag off the data
 * index is the two tabs it has always been, and nothing about it is
 * conditional.
 */
export function dataIndexTabs(withConversions: boolean): readonly DataIndexTab[] {
  return withConversions ? [...DATA_INDEX_TABS, CONVERSIONS_TAB] : DATA_INDEX_TABS;
}

/**
 * The list a URL segment names, or null when it names none of them.
 *
 * `withConversions` is the flag, and a segment it does not cover is a segment
 * this page has no list for — which is how a held `/data-index/conversions`
 * link behaves like any other unknown segment when the flag is off.
 */
export function asDataIndexListType(value: string, withConversions = false): DataIndexListType | null {
  return dataIndexTabs(withConversions).find((tab) => tab.type === value)?.type ?? null;
}

export function activeTabFor(type: DataIndexType | DataIndexListType): DataIndexListType {
  if (type === 'events' || type === CONVERSIONS_LIST_TYPE) {
    return type;
  }
  return 'attributes';
}

/**
 * The `journeys` list a migrated one falls back to when the data index is not
 * served by this app.
 *
 * Conversions has no counterpart there, so it falls back to the list the
 * embedded app lands on rather than to a URL that answers nothing.
 */
export function legacyListTypeFor(type: DataIndexListType): DataIndexType {
  return type === CONVERSIONS_LIST_TYPE ? 'attributes' : type;
}

/**
 * The object type a browser reads under, as the select encodes it: the object
 * type id, with `:relationship` appended for the relationship side. Profile
 * attributes are `0`, which is also how the wire spells "no object type".
 */
export const PROFILE_SELECTION = '0';

export function encodeScopeSelection(scope: AttributeScope): string {
  if (scope.kind === 'profile') {
    return PROFILE_SELECTION;
  }
  const objectTypeId = String(scope.objectTypeId);
  return scope.kind === 'relationship' ? `${objectTypeId}:relationship` : objectTypeId;
}

/**
 * What choosing a scope writes to the URL.
 *
 * The browser starts again from its first page, and the tags and sources
 * chosen for the previous scope are dropped: they are the other scope's ids
 * and would narrow the new list to nothing. The search and the in-use view
 * are not named here, so they carry over.
 */
export function scopeSelectionParams(value: string) {
  const next = decodeScopeSelection(value);

  return {
    page: 1,
    searchTagIds: null,
    searchSourceIds: null,
    // Two params, which is what the page this replaces declares
    // (`app/pods/data-index/type/list/route.ts:36-43`). Both apps serve these
    // URLs while the flag rolls out, so a link written here has to be one
    // Ember can read.
    objectType: next.kind === 'profile' ? PROFILE_SELECTION : String(next.objectTypeId),
    relationship: next.kind === 'relationship' ? 'true' : null,
  };
}

/** The scope a select value names, or the profile scope when it names none. */
export function decodeScopeSelection(value: string): AttributeScope {
  const [id, suffix] = value.split(':');
  const objectTypeId = Number(id);
  if (!Number.isInteger(objectTypeId) || objectTypeId <= 0) {
    return { kind: 'profile' };
  }
  return suffix === 'relationship' ? { kind: 'relationship', objectTypeId } : { kind: 'object', objectTypeId };
}
